import { NavLink } from "react-router-dom";
import { useFeature } from "../hooks/useFeature";

interface Tab {
  label: string;
  shortLabel?: string;
  to: string;
  end?: boolean;
  /** SVG path data for the mobile bottom-bar icon (24×24 viewBox). */
  icon: string;
  /** Whether to include in the mobile bottom bar. Tabs with false are
   *  still in the desktop strip and the drawer. */
  showInBottomBar: boolean;
}

const TABS: Tab[] = [
  {
    label: "Home",
    to: "/",
    end: true,
    icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
    showInBottomBar: true,
  },
  {
    label: "Events",
    to: "/events",
    icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    showInBottomBar: true,
  },
  {
    label: "News",
    to: "/news",
    icon: "M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h6",
    showInBottomBar: true,
  },
  {
    label: "Family Tree",
    shortLabel: "Tree",
    to: "/tree",
    icon: "M12 3v18m0-18c-3 0-6 3-6 6m6-6c3 0 6 3 6 6m-9 3c-2 0-4 2-4 4m7-4c2 0 4 2 4 4",
    showInBottomBar: true,
  },
  {
    label: "World",
    to: "/world",
    icon: "M12 2a10 10 0 100 20 10 10 0 000-20zm0 0c2.5 0 4.5 4.5 4.5 10S14.5 22 12 22 7.5 17.5 7.5 12 9.5 2 12 2zM2 12h20",
    showInBottomBar: true,
  },
  {
    label: "Photos & Videos",
    shortLabel: "Photos",
    to: "/gallery",
    icon: "M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9zm9 8a4 4 0 100-8 4 4 0 000 8z",
    showInBottomBar: false,
  },
  {
    label: "Albums",
    to: "/albums",
    icon: "M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zM5 5h14v8l-3.5-3.5L11 14l-3-3-3 3V5z",
    showInBottomBar: false,
  },
];

/**
 * Renders two nav elements:
 *   1. A horizontal text-link strip (desktop, hidden on mobile)
 *   2. A fixed bottom icon bar (mobile, hidden on desktop)
 *
 * CSS in index.css toggles visibility at the 720 px breakpoint.
 */
export function TabNav() {
  // Null while /me is in flight, false when the user is not enrolled.
  const aiEnabled = useFeature("ai_chat");

  return (
    <>
      {/* Desktop: horizontal text strip (existing design) */}
      <nav className="tab-nav tab-nav--desktop" aria-label="Primary sections">
        <div className="tab-nav__inner">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              aria-label={t.label}
              className={({ isActive }) =>
                `tab-nav__link ${isActive ? "is-active" : ""}`
              }
            >
              <span className="tab-nav__label--full">{t.label}</span>
              {t.shortLabel && (
                <span className="tab-nav__label--short">{t.shortLabel}</span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Mobile: fixed bottom icon bar */}
      <nav className="tab-bar tab-bar--mobile" aria-label="Primary sections">
        {TABS.filter((t) => t.showInBottomBar).map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            aria-label={t.label}
            className={({ isActive }) =>
              `tab-bar__item ${isActive ? "is-active" : ""}`
            }
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={t.icon} />
            </svg>
            <span className="tab-bar__label">
              {t.shortLabel ?? t.label}
            </span>
          </NavLink>
        ))}
        {/* AI chat button — same position as the floating bubble but
         *  integrated into the bar so it doesn't overlap.
         *
         *  Gated here rather than left to ChatBot. ChatBot returns null when
         *  the user is not enrolled, which hides the PANEL but not this
         *  button, so anybody without the beta feature tapped a button that
         *  swallowed the tap and did nothing. On a phone this is the only
         *  way in — the floating bubble is hidden below 720px — so it looked
         *  like the AI was broken on their device rather than switched off.
         *
         *  `=== true` on purpose: useFeature returns null while /me loads,
         *  and a button that appears a moment later is worse than one that
         *  was never there. */}
        {aiEnabled === true && (
          <button
            type="button"
            className="tab-bar__item tab-bar__ai-btn"
            aria-label="Family AI chat"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("ai-chat:toggle"))
            }
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
            >
              <text
                x="16"
                y="26"
                textAnchor="middle"
                fill="currentColor"
                style={{
                  fontFamily:
                    '"Noto Ethiopic A", "Noto Sans Ethiopic", "Nyala", "Kefa", system-ui, serif',
                  fontSize: "30px",
                  fontWeight: 600,
                }}
              >
                አ
              </text>
            </svg>
            <span className="tab-bar__label">AI</span>
          </button>
        )}
      </nav>
    </>
  );
}
